import { useState, useEffect, useRef } from "react";
import { Star, ExternalLink, PenLine, Quote, ChevronLeft, ChevronRight } from "lucide-react";
import { useLang } from "@/i18n/LanguageContext";

// Odkazy na Google profil EffectDeal
const GOOGLE_PROFILE_URL = "https://www.google.com/maps/place/effect+deal/@50.2370476,15.0201964,17z/data=!4m8!3m7!1s0x470c05bafbefe94d:0xee90fe74468c14c6!8m2!3d50.2370476!4d15.0227713!9m1!1b1!16s%2Fg%2F11n4mlvfsc";
const GOOGLE_REVIEW_URL = "https://g.page/r/CcYUjEZ0_pDuEAE/review";
const GOOGLE_RATING = 5.0;
const GOOGLE_REVIEW_COUNT = 14;

// Reálné recenze klientů z Google (texty v původním jazyce).
const REVIEWS = [
  {
    name: "Jakub Půček",
    rating: 5,
    text: "Super domluva, všechno rychlý a bez zbytečných keců. Auto čistý, voňavý a v top stavu. Určitě doporučuju!",
  },
  {
    name: "Marek Daňko",
    rating: 5,
    text: "Pri návšteve CZ som sa obrátil na túto autopožičovňu a iba odporúčam. Už od začiatku bola dohoda veľmi rýchla, efektná a príjemná zo strany majiteľa, auto bolo v krásnom stave a ako bonus aj darček ako pozornosť. 10z10",
  },
  {
    name: "Petr Novák",
    rating: 5,
    text: "Rychlý jednání!! Vše super :)",
  },
];

const AVATAR_COLORS = [
  "bg-orange-500/25 text-orange-300",
  "bg-emerald-500/25 text-emerald-300",
  "bg-amber-500/25 text-amber-300",
  "bg-sky-500/25 text-sky-300",
  "bg-rose-500/25 text-rose-300",
];
const initials = (name) =>
  name.split(" ").map((w) => w[0]).join("").slice(0, 2).toUpperCase();

const TXT = {
  cs: {
    eyebrow: "Reference", title: "Co o nás říkají klienti",
    subtitle: `Hodnocení na Googlu · ${GOOGLE_REVIEW_COUNT} recenzí`,
    view: "Zobrazit recenze na Googlu", write: "Ohodnoťte nás",
    live: "Živá hodnocení", verified: "Ověřeno Googlem", prev: "Předchozí", next: "Další",
  },
  en: {
    eyebrow: "Reviews", title: "What our clients say",
    subtitle: `Rating on Google · ${GOOGLE_REVIEW_COUNT} reviews`,
    view: "See reviews on Google", write: "Rate us",
    live: "Live reviews", verified: "Verified by Google", prev: "Previous", next: "Next",
  },
  de: {
    eyebrow: "Bewertungen", title: "Was unsere Kunden sagen",
    subtitle: `Bewertung bei Google · ${GOOGLE_REVIEW_COUNT} Bewertungen`,
    view: "Bewertungen bei Google ansehen", write: "Bewerten Sie uns",
    live: "Live-Bewertungen", verified: "Von Google verifiziert", prev: "Zurück", next: "Weiter",
  },
};

const Stars = ({ value = 5, className = "w-4 h-4" }) => (
  <div className="inline-flex items-center gap-0.5" aria-label={`${value} / 5`}>
    {[1, 2, 3, 4, 5].map((i) => (
      <Star key={i} className={`${className} ${i <= Math.round(value) ? "text-amber-400 fill-amber-400" : "text-zinc-600"}`} />
    ))}
  </div>
);

const ReviewsSection = () => {
  const { lang } = useLang();
  const t = TXT[lang] || TXT.cs;
  const [idx, setIdx] = useState(0);
  const [paused, setPaused] = useState(false);
  const total = REVIEWS.length;

  // Auto-rotace recenzí (každé 4 s), pauza při najetí myší / fokusu.
  useEffect(() => {
    if (paused || total <= 1) return;
    const id = setInterval(() => setIdx((i) => (i + 1) % total), 4000);
    return () => clearInterval(id);
  }, [paused, total]);

  const go = (n) => setIdx((n + total) % total);
  // 3 karty od aktuálního indexu (na desktopu se zobrazí vedle sebe, mění se s rotací).
  const visible = Array.from({ length: Math.min(3, total) }, (_, k) => REVIEWS[(idx + k) % total]);

  return (
    <section id="reviews" className="py-16 sm:py-24 px-4 sm:px-6 relative" data-testid="reviews-section">
      <div className="max-w-4xl mx-auto">
        <div className="text-center mb-10 sm:mb-14">
          <div className="inline-flex items-center gap-2 text-orange-400 font-medium tracking-widest uppercase text-xs sm:text-sm mb-3 sm:mb-4">
            <Star className="w-4 h-4 sm:w-5 sm:h-5" />
            <span>{t.eyebrow}</span>
          </div>
          <h2 className="section-title text-2xl sm:text-3xl lg:text-4xl">{t.title}</h2>
        </div>

        {/* Google rating band */}
        <div className="glass rounded-2xl p-6 sm:p-8 flex flex-col sm:flex-row items-center justify-between gap-6" data-testid="google-rating-band">
          <div className="flex items-center gap-4">
            <svg className="w-10 h-10 shrink-0" viewBox="0 0 48 48" aria-hidden="true">
              <path fill="#FFC107" d="M43.611 20.083H42V20H24v8h11.303c-1.649 4.657-6.08 8-11.303 8-6.627 0-12-5.373-12-12s5.373-12 12-12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 12.955 4 4 12.955 4 24s8.955 20 20 20 20-8.955 20-20c0-1.341-.138-2.65-.389-3.917z" />
              <path fill="#FF3D00" d="M6.306 14.691l6.571 4.819C14.655 15.108 18.961 12 24 12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 16.318 4 9.656 8.337 6.306 14.691z" />
              <path fill="#4CAF50" d="M24 44c5.166 0 9.86-1.977 13.409-5.192l-6.19-5.238C29.211 35.091 26.715 36 24 36c-5.202 0-9.619-3.317-11.283-7.946l-6.522 5.025C9.505 39.556 16.227 44 24 44z" />
              <path fill="#1976D2" d="M43.611 20.083H42V20H24v8h11.303c-.792 2.237-2.231 4.166-4.087 5.571l.003-.002 6.19 5.238C36.971 39.205 44 34 44 24c0-1.341-.138-2.65-.389-3.917z" />
            </svg>
            <div className="text-left">
              <div className="flex items-center gap-2">
                <span className="text-3xl font-bold font-['Cabinet_Grotesk']" data-testid="google-rating-value">{GOOGLE_RATING.toFixed(1).replace(".", ",")}</span>
                <Stars value={GOOGLE_RATING} className="w-4 h-4" />
              </div>
              <p className="text-xs text-zinc-500 mt-0.5">{t.subtitle}</p>
            </div>
          </div>
          <div className="flex flex-wrap items-center justify-center gap-3">
            <a href={GOOGLE_PROFILE_URL} target="_blank" rel="noopener noreferrer" className="btn-outline inline-flex items-center gap-2 text-sm" data-testid="reviews-view-btn">
              <ExternalLink className="w-4 h-4" /> {t.view}
            </a>
            <a href={GOOGLE_REVIEW_URL} target="_blank" rel="noopener noreferrer" className="btn-accent inline-flex items-center gap-2 text-sm" data-testid="reviews-write-btn">
              <PenLine className="w-4 h-4" /> {t.write}
            </a>
          </div>
        </div>

        {/* Live rotating widget */}
        <div
          className="mt-6"
          onMouseEnter={() => setPaused(true)}
          onMouseLeave={() => setPaused(false)}
          onFocusCapture={() => setPaused(true)}
          onBlurCapture={() => setPaused(false)}
          data-testid="reviews-carousel"
        >
          {/* Live indicator + controls */}
          <div className="flex items-center justify-between mb-3">
            <span className="inline-flex items-center gap-2 text-xs font-medium text-emerald-300" data-testid="reviews-live-indicator">
              <span className="relative flex h-2.5 w-2.5">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-60" />
                <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-emerald-400" />
              </span>
              {t.live}
            </span>
            <div className="flex items-center gap-1.5">
              <button type="button" onClick={() => go(idx - 1)} aria-label={t.prev} className="w-8 h-8 rounded-full border border-white/10 bg-white/5 text-zinc-400 hover:text-white hover:border-white/20 inline-flex items-center justify-center transition-colors" data-testid="reviews-prev">
                <ChevronLeft className="w-4 h-4" />
              </button>
              <button type="button" onClick={() => go(idx + 1)} aria-label={t.next} className="w-8 h-8 rounded-full border border-white/10 bg-white/5 text-zinc-400 hover:text-white hover:border-white/20 inline-flex items-center justify-center transition-colors" data-testid="reviews-next">
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* Cards (re-mounted on idx change for the entrance animation = the "live" feel) */}
          <div key={idx} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4 animate-fade-in-up" data-testid="reviews-grid">
            {visible.map((r, i) => (
              <div key={`${idx}-${i}`} className={`glass rounded-xl p-5 flex flex-col gap-3 ${i > 0 ? "hidden lg:flex" : ""}`} data-testid={`review-card-${i}`}>
                <div className="flex items-center justify-between">
                  <Quote className="w-6 h-6 text-orange-400/60" />
                  <span className="text-[10px] uppercase tracking-wider text-zinc-500">{t.verified}</span>
                </div>
                <p className="text-sm text-zinc-300 leading-relaxed flex-1">{r.text}</p>
                <div className="flex items-center justify-between pt-2 border-t border-white/5">
                  <span className="inline-flex items-center gap-2">
                    <span className={`w-7 h-7 rounded-full flex items-center justify-center text-[11px] font-bold ${AVATAR_COLORS[(idx + i) % AVATAR_COLORS.length]}`}>
                      {initials(r.name)}
                    </span>
                    <span className="text-sm font-medium text-zinc-200">{r.name}</span>
                  </span>
                  <Stars value={r.rating} className="w-3.5 h-3.5" />
                </div>
              </div>
            ))}
          </div>

          {/* Dots */}
          <div className="flex items-center justify-center gap-2 mt-5" data-testid="reviews-dots">
            {REVIEWS.map((_, i) => (
              <button
                key={i}
                type="button"
                onClick={() => setIdx(i)}
                aria-label={`Recenze ${i + 1}`}
                className={`h-1.5 rounded-full transition-all ${i === idx ? "w-6 bg-orange-400" : "w-1.5 bg-white/20 hover:bg-white/40"}`}
                data-testid={`reviews-dot-${i}`}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default ReviewsSection;
